import LocationsDockHoursInput from "../Inputs/LocationsDockHoursInput";
import NotesQuestionBubble from "@/Shared/Locations/Bubbles/NotesQuestionBubble";
import { locationsErrorMessagesForLocation } from "../Validators/locationsErrorMessagesForLocation";
import type { ChangeEvent } from "react";
import { useState } from "react";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { hasLiftGateAccessorial } from "@/Shared/Locations/Helpers/loadingCapabilities";
import LoadingCapabilitiesQuestionBubble from "@/Shared/Locations/Bubbles/LoadingCapabilitiesQuestionBubble";
import LoadingCapabilitiesDropdown from "@/Shared/Locations/Dropdowns/LoadingCapabilitiesDropdown";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextArea } from "@freightsimple/ui-core/Controls/TextArea";

interface LocationsAddLocationDialogOtherPage {
  location: AddLocationLocationType;
  setLocation: (_: AddLocationLocationType) => void;
  onDone: () => Promise<void>;
  onPrevious: () => void;
}

function LocationsAddLocationDialogOtherPage(
  props: LocationsAddLocationDialogOtherPage,
) {
  const { location, setLocation, onDone, onPrevious } = props;
  const [forceValidation, setForceValidation] = useState(false);

  // TODO: False
  const locationErrorMessages = locationsErrorMessagesForLocation(
    location,
    false,
  );

  const locationHasDock =
    location.locationHasDock ?? LoadingCapabilitiesAvailability.Unknown;
  const locationHasForklift =
    location.locationHasForklift ?? LoadingCapabilitiesAvailability.Unknown;

  const hasLiftGate = hasLiftGateAccessorial(location.accessorials);

  const liftGateRequired =
    locationHasDock === LoadingCapabilitiesAvailability.No &&
    locationHasForklift === LoadingCapabilitiesAvailability.No &&
    !hasLiftGate;

  function setNotes(notes: string) {
    setLocation({
      ...location,
      notes,
    });
  }

  function setHoursOpenFrom(hoursFrom: string) {
    setLocation({
      ...location,
      hours: {
        openFrom: hoursFrom,
        openUntil: location.hours.openUntil,
      },
    });
  }

  function setHoursOpenUntil(hoursUntil: string) {
    setLocation({
      ...location,
      hours: {
        openFrom: location.hours.openFrom,
        openUntil: hoursUntil,
      },
    });
  }

  function handleLocationHasDockChange(value: LoadingCapabilitiesAvailability) {
    setLocation({
      ...location,
      locationHasDock: value,
    });
  }

  function handleLocationHasForkliftChange(
    value: LoadingCapabilitiesAvailability,
  ) {
    setLocation({
      ...location,
      locationHasForklift: value,
    });
  }

  function getLocationHasDockError(): string | undefined {
    // If lift gate is present, we don't need to ask about dock
    if (hasLiftGate) {
      return undefined;
    }

    if (locationHasDock === LoadingCapabilitiesAvailability.Unknown) {
      return "Please select if this location has a shipping dock";
    }
    return undefined;
  }

  function getLocationHasForkliftError(): string | undefined {
    // If lift gate is present, we don't need to ask about forklift
    if (hasLiftGate) {
      return undefined;
    }

    if (locationHasDock === LoadingCapabilitiesAvailability.No) {
      if (locationHasForklift === LoadingCapabilitiesAvailability.Unknown) {
        return "Please select if this location has a forklift";
      }
    }
    return undefined;
  }

  function isValid() {
    if (locationErrorMessages.hours !== undefined) {
      return false;
    }

    if (liftGateRequired) {
      return false;
    }

    if (getLocationHasDockError() !== undefined) {
      return false;
    }

    if (getLocationHasForkliftError() !== undefined) {
      return false;
    }

    return true;
  }

  async function onNext() {
    if (isValid()) {
      await onDone();
    } else {
      setForceValidation(true);
    }
  }

  return (
    // No width here: the wizard body already pins the page width.
    <Stack gap={8}>
      <Stack gap={0}>
        <T size="base" weight="bold" color="foreground">
          Default Hours
          <NotesQuestionBubble />
        </T>
        <LocationsDockHoursInput
          openFrom={props.location.hours?.openFrom}
          openUntil={props.location.hours?.openUntil}
          setOpenFrom={setHoursOpenFrom}
          setOpenUntil={setHoursOpenUntil}
          errorMessage={locationErrorMessages.hours}
          forceValidation={forceValidation}
        />
      </Stack>

      <Stack gap={0}>
        <T size="base" weight="bold" color="foreground">
          Notes
          <NotesQuestionBubble />
        </T>
        {/* No width wrapper: the Stack lets the textarea fill the dialog
         * body, which is the width it was pinned to anyway. */}
        <TextArea
          rows={4}
          value={props.location.notes}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            setNotes(e.currentTarget.value);
          }}
        />
      </Stack>

      {!hasLiftGate && (
        <Stack gap={4}>
          <T size="base" weight="bold" color="foreground">
            Loading
            <LoadingCapabilitiesQuestionBubble />
          </T>
          <LoadingCapabilitiesDropdown
            question="Does this location have a shipping dock?"
            value={locationHasDock}
            onChange={handleLocationHasDockChange}
            errorMessage={getLocationHasDockError()}
            forceValidation={forceValidation}
            data-testid="shipping-dock-question"
          />
          {locationHasDock === LoadingCapabilitiesAvailability.No && (
            <LoadingCapabilitiesDropdown
              question="Does this location have a forklift?"
              value={locationHasForklift}
              onChange={handleLocationHasForkliftChange}
              errorMessage={getLocationHasForkliftError()}
              forceValidation={forceValidation}
            />
          )}
          {/* The banner lives in this section because it can only ever show
           * here: requiring a lift gate means there isn't one booked, which
           * is the same condition that shows the loading questions. */}
          {liftGateRequired && (
            <Banner variant="danger">
              This location requires a lift gate service since there is no dock
              or forklift available. Please enable lift gate for this location.
            </Banner>
          )}
        </Stack>
      )}

      <Row justify="between">
        <Button onClick={onPrevious}>Previous</Button>
        <Button
          primary
          disabled={liftGateRequired}
          disabledMessage="Confirm 'Yes' on lift gate above to continue"
          onClick={onNext}
        >
          Done
        </Button>
      </Row>
    </Stack>
  );
}

export default LocationsAddLocationDialogOtherPage;
